import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type SyntheticEvent } from "react";
import {
  executeApplicationCallbackAction,
  getApplicationCallbackSecretMetadata,
  listApplicationCallbacks,
  readApplicationCallbackSecret,
  rotateApplicationCallbackSecret,
  saveApplicationCallback,
  type Application,
  type ApplicationCallbackConfiguration,
  type ApplicationCallbackSecretMetadata,
  type ApplicationCallbackType,
} from "../api";
import { platformCallbackUrlIssue } from "../callback-url";
import { CallbackSecretPanel, type CallbackSecretDisplay } from "./CallbackSecretPanel";
import { ManagementHeading, TimeValue } from "./ManagementCommon";
import { Button } from "./ui/Button";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { Icon, type IconName } from "./ui/Icon";

const callbackTypes: readonly ApplicationCallbackType[] = [
  "MESSAGE_BEFORE_SEND",
  "MESSAGE_AFTER_SEND",
  "USER_ONLINE",
  "USER_OFFLINE",
];

interface CallbackMetadata {
  readonly title: string;
  readonly description: string;
  readonly icon: IconName;
}

interface SecretRequest {
  readonly sequence: number;
  readonly appId: string;
  readonly deadline: number;
}

const metadata: Record<ApplicationCallbackType, CallbackMetadata> = {
  MESSAGE_BEFORE_SEND: {
    title: "消息发送前",
    description: "同步审核消息；失败或超时会阻断发送",
    icon: "warning",
  },
  MESSAGE_AFTER_SEND: {
    title: "消息发送后",
    description: "消息可靠接受后异步、至少一次投递",
    icon: "activity",
  },
  USER_ONLINE: {
    title: "用户上线",
    description: "用户有效设备数从 0 变为 1 时触发",
    icon: "users",
  },
  USER_OFFLINE: {
    title: "用户下线",
    description: "最后设备离线并超过重连宽限期后触发",
    icon: "logout",
  },
};

export function CallbackConfigurationView({ application, applications, onSelect, onNotice, onError }: {
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly onSelect: (application: Application) => void;
  readonly onNotice: (message: string) => void;
  readonly onError: (message: string) => void;
}) {
  const [callbacks, setCallbacks] = useState<readonly ApplicationCallbackConfiguration[]>([]);
  const [selectedType, setSelectedType] = useState<ApplicationCallbackType>("MESSAGE_BEFORE_SEND");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<CallbackSecretDisplay | null>(null);
  const [keyMetadata, setKeyMetadata] = useState<ApplicationCallbackSecretMetadata | null>(null);
  const [keyLoading, setKeyLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<"disable" | "rotate-secret" | "reveal-secret" | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const mounted = useRef(false);
  const secretSequence = useRef(0);
  const secretTimer = useRef<number | undefined>(undefined);
  const secretReadPending = useRef(false);
  const mutationPending = useRef(false);
  const currentScope = useRef(application?.appId ?? null);
  const canReadSecret = application?.permissions.includes("im.callback.secret.read") ?? false;
  const canRotateSecret = application?.permissions.includes("im.callback.secret.rotate") ?? false;
  const currentCanReadSecret = useRef(canReadSecret);
  const currentCanRotateSecret = useRef(canRotateSecret);
  currentCanReadSecret.current = canReadSecret;
  currentCanRotateSecret.current = canRotateSecret;
  // Invalidate during the scope-changing render, not only after its effects run.
  if (currentScope.current !== (application?.appId ?? null)) {
    currentScope.current = application?.appId ?? null;
    secretSequence.current += 1;
  }
  const selected = useMemo(
    () => callbacks.find((callback) => callback.callbackType === selectedType) ?? null,
    [callbacks, selectedType],
  );
  const applicationKey = keyMetadata?.appId === application?.appId ? keyMetadata : null;
  const reload = useCallback(() => { setReloadKey((value) => value + 1); }, []);
  const clearSecret = useCallback(() => {
    secretSequence.current += 1;
    if (secretTimer.current !== undefined) window.clearTimeout(secretTimer.current);
    secretTimer.current = undefined;
    secretReadPending.current = false;
    mutationPending.current = false;
    setSecret(null);
    setBusy(false);
  }, []);

  useLayoutEffect(() => {
    mounted.current = true;
    const leave = (): void => { clearSecret(); setConfirmation(null); };
    const visibility = (): void => { if (document.visibilityState === "hidden") leave(); };
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      mounted.current = false;
      secretSequence.current += 1;
      if (secretTimer.current !== undefined) window.clearTimeout(secretTimer.current);
      window.removeEventListener("pagehide", leave);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [clearSecret]);

  useEffect(() => {
    setCallbacks([]);
    setKeyMetadata(null);
    setSelectedType("MESSAGE_BEFORE_SEND");
  }, [application?.appId]);

  useLayoutEffect(() => { clearSecret(); setConfirmation(null); }, [application?.appId, selectedType, clearSecret]);

  function activeSecretRequest(request: SecretRequest): boolean {
    return mounted.current && secretSequence.current === request.sequence &&
      currentScope.current === request.appId;
  }

  function currentSecretRequest(request: SecretRequest): boolean {
    return activeSecretRequest(request) && Date.now() < request.deadline;
  }

  function beginSecretRequest(appId: string, pending = false): SecretRequest {
    clearSecret();
    const request = { sequence: secretSequence.current, appId, deadline: Date.now() + 60_000 };
    if (pending) setSecret({ appId, value: null, version: null });
    secretTimer.current = window.setTimeout(() => {
      if (secretSequence.current === request.sequence) clearSecret();
    }, 60_000);
    return request;
  }

  function displaySecret(request: SecretRequest, value: string, version: number | null): void {
    if (!currentSecretRequest(request)) return;
    if (secretTimer.current !== undefined) window.clearTimeout(secretTimer.current);
    setSecret({ appId: request.appId, value, version });
    secretTimer.current = window.setTimeout(() => {
      if (secretSequence.current === request.sequence) clearSecret();
    }, 60_000);
  }

  function selectApplication(value: Application): void {
    clearSecret();
    setConfirmation(null);
    onSelect(value);
  }

  function selectType(value: ApplicationCallbackType): void {
    clearSecret();
    setConfirmation(null);
    setSelectedType(value);
  }

  useEffect(() => {
    if (application === null) return;
    let active = true;
    setLoading(true);
    void listApplicationCallbacks(application.appId)
      .then((items) => { if (active) setCallbacks(items); })
      .catch((cause: unknown) => { if (active) onError(errorMessage(cause)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [application?.appId, onError, reloadKey]);

  useEffect(() => {
    if (application === null) return;
    let active = true;
    setKeyLoading(true);
    void getApplicationCallbackSecretMetadata(application.appId)
      .then((key) => { if (active) setKeyMetadata(key); })
      .catch(() => {
        if (!active) return;
        setKeyMetadata(null);
        onError("无法加载应用回调密钥状态，请稍后重试");
      })
      .finally(() => { if (active) setKeyLoading(false); });
    return () => { active = false; };
  }, [application?.appId, onError, reloadKey]);

  async function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    if (application === null || mutationPending.current || secretReadPending.current) return;
    const data = new FormData(event.currentTarget);
    const callbackUrl = formString(data, "callbackUrl").trim();
    const issue = platformCallbackUrlIssue(callbackUrl);
    if (issue !== null) {
      onError(issue);
      return;
    }
    const request = beginSecretRequest(application.appId);
    mutationPending.current = true;
    setBusy(true);
    try {
      const result = await saveApplicationCallback(application.appId, selectedType, {
        callbackUrl,
        timeoutMs: Number(data.get("timeoutMs")),
        maxAttempts: selectedType === "MESSAGE_BEFORE_SEND" ? 1 : Number(data.get("maxAttempts")),
        subscribedConversationTypes: formStrings(data, "conversationTypes"),
        subscribedMessageTypes: formStrings(data, "messageTypes"),
        subscribedMessageSources: formStrings(data, "messageSources"),
        ...(selected === null ? {} : { version: selected.version }),
      });
      if (!currentSecretRequest(request)) return;
      // A callback's configuration version is not the independent application-key version.
      if (result.secret !== undefined) displaySecret(request, result.secret, null);
      setConfirmation(null);
      onNotice(selected === null
        ? "回调配置已创建；当前应用的四类回调共用应用回调密钥"
        : "回调配置已保存，需重新验证后启用");
      reload();
    } catch {
      if (currentSecretRequest(request)) onError("保存回调配置失败，请刷新配置后重试");
    } finally {
      if (activeSecretRequest(request)) { mutationPending.current = false; setBusy(false); }
    }
  }

  async function action(value: "enable" | "disable" | "verify"): Promise<void> {
    if (application === null || selected === null || mutationPending.current || secretReadPending.current) return;
    const request = beginSecretRequest(application.appId);
    mutationPending.current = true;
    setBusy(true);
    try {
      await executeApplicationCallbackAction(
        application.appId,
        selected.callbackType,
        value,
      );
      if (!currentSecretRequest(request)) return;
      setConfirmation(null);
      onNotice(value === "verify"
        ? "测试事件验证成功"
        : value === "enable"
            ? "回调已启用"
            : "回调已停用");
      reload();
    } catch (cause) {
      if (currentSecretRequest(request)) onError(errorMessage(cause));
    } finally {
      if (activeSecretRequest(request)) { mutationPending.current = false; setBusy(false); }
    }
  }

  async function revealSecret(): Promise<void> {
    if (application === null || !applicationKey?.configured || !canReadSecret || busy || mutationPending.current || secretReadPending.current) return;
    const request = beginSecretRequest(application.appId, true);
    secretReadPending.current = true;
    setConfirmation(null);
    try {
      const result = await readApplicationCallbackSecret(request.appId);
      if (!currentSecretRequest(request) || !currentCanReadSecret.current) return;
      displaySecret(request, result.secret, result.version);
      secretReadPending.current = false;
    } catch {
      if (!currentSecretRequest(request)) return;
      clearSecret();
      // Never forward an upstream body or parser error to a persistent global banner.
      onError("无法读取应用回调密钥，请检查权限或稍后重新确认后重试");
    }
  }

  async function rotateSecret(): Promise<void> {
    if (application === null || !applicationKey?.configured || !canRotateSecret || busy || mutationPending.current || secretReadPending.current) return;
    const request = beginSecretRequest(application.appId);
    mutationPending.current = true;
    setBusy(true);
    try {
      const result = await rotateApplicationCallbackSecret(request.appId, applicationKey.version ?? undefined);
      if (!currentSecretRequest(request) || !currentCanRotateSecret.current) return;
      displaySecret(request, result.secret, result.version);
      setKeyMetadata({ appId: request.appId, configured: true, version: result.version,
        previousSecretValidUntil: result.previousSecretValidUntil ?? null });
      setConfirmation(null);
      onNotice("应用回调密钥已轮换；本应用全部已配置回调共用新密钥，类型配置保持不变");
      reload();
    } catch {
      if (currentSecretRequest(request)) onError("轮换应用回调密钥失败，请刷新密钥状态后重试");
    } finally {
      if (activeSecretRequest(request)) { mutationPending.current = false; setBusy(false); }
    }
  }

  const selectedMetadata = metadata[selectedType];
  const selectedState = callbackState(selected);
  const visibleSecret = secret !== null && secret.appId === application?.appId ? secret : null;
  const readingSecret = visibleSecret !== null && visibleSecret.value === null;

  return (
    <div className="management-page callback-page">
      <ManagementHeading
        title="回调配置"
        description="按应用配置同步消息审核、可靠消息通知和聚合用户状态事件。"
        applications={applications}
        selected={application}
        onSelect={selectApplication}
      />
      {application === null ? (
        <div className="empty-page"><strong>请先创建应用</strong><p>回调配置严格归属于单个应用。</p></div>
      ) : (
        <div className="callback-workspace">
          <section className="surface-card callback-app-secret-card" aria-label="应用回调密钥">
            <header>
              <div><h3>应用回调密钥</h3><p>当前应用的消息发送前、消息发送后、用户上线和用户下线回调共用一个签名密钥。</p></div>
              <span className={`management-tag ${applicationKey?.configured ? "tag-success" : "tag-muted"}`}>{keyLoading ? "正在加载" : applicationKey?.configured ? "已配置" : applicationKey === null ? "状态未知" : "未配置"}</span>
            </header>
            <p><code>{application.appId}</code>{applicationKey?.version === null || applicationKey?.version === undefined ? null : ` · 密钥 v${applicationKey.version}`}</p>
            <p>各类型的回调地址、启用状态、超时、重试和事件过滤独立管理；保存类型配置不会轮换共享密钥。</p>
            {applicationKey !== null && !applicationKey.configured ? <p>保存任意一种回调后自动生成应用回调密钥，后续新增类型复用同一密钥。</p> : null}
            {applicationKey?.previousSecretValidUntil === null || applicationKey?.previousSecretValidUntil === undefined ? null : <p>旧密钥兼容窗口截止：<TimeValue value={applicationKey.previousSecretValidUntil} /></p>}
            <div className="callback-secondary-actions">
              {canReadSecret ? <Button disabled={busy || readingSecret || keyLoading || !applicationKey?.configured} type="button" variant="ghost" onClick={() => { clearSecret(); setConfirmation("reveal-secret"); }}>查看密钥</Button> : null}
              {canRotateSecret ? <Button disabled={busy || readingSecret || keyLoading || !applicationKey?.configured} type="button" variant="ghost" onClick={() => { setConfirmation("rotate-secret"); }}>轮换密钥</Button> : null}
            </div>
            {visibleSecret === null ? null : <CallbackSecretPanel key={secretSequence.current} display={visibleSecret} onClose={clearSecret} />}
          </section>
          <section className="callback-overview-grid" aria-label="回调类型">
            {callbackTypes.map((type) => {
              const callback = callbacks.find((item) => item.callbackType === type);
              const state = callbackState(callback ?? null);
              const item = metadata[type];
              const isSelected = selectedType === type;
              return (
                <button
                  aria-pressed={isSelected}
                  className={`callback-type-card ${isSelected ? "selected" : ""}`}
                  key={type}
                  type="button"
                  onClick={() => { selectType(type); }}
                >
                  <span className="callback-type-card-header">
                    <span className="callback-type-icon"><Icon name={item.icon} size={17} /></span>
                    <span className={`management-tag ${state.className}`}>{state.label}</span>
                  </span>
                  <span className="callback-type-copy">
                    <strong>{item.title}</strong>
                    <small>{item.description}</small>
                  </span>
                  <span className="callback-type-code">
                    <code>{type}</code><Icon name="chevron-right" size={15} />
                  </span>
                </button>
              );
            })}
          </section>

          <section className="data-card callback-editor">
            <header className="data-card-header callback-editor-header">
              <div className="callback-editor-title">
                <span><Icon name={selectedMetadata.icon} size={18} /></span>
                <div><h3>{selectedMetadata.title}</h3><p>{selectedMetadata.description}</p></div>
              </div>
              <div className="callback-status-meta">
                <span className={`management-tag ${selectedState.className}`}>{selectedState.label}</span>
                {selected === null ? null : (
                  <span className="callback-verification-time">
                    <small>最近验证</small><TimeValue value={selected.lastVerifiedAt} />
                  </span>
                )}
              </div>
            </header>

            {loading ? (
              <div className="management-state"><span className="loader" /><strong>正在加载回调配置</strong></div>
            ) : (
              <form
                key={`${selectedType}:${selected?.version ?? 0}`}
                className="callback-form"
                onSubmit={(event) => { void submit(event); }}
              >
                <section className="callback-form-section">
                  <header className="callback-form-section-heading">
                    <div><h4>投递端点</h4><p>配置接收地址、单次超时和失败重试边界。</p></div>
                    <code>HTTP / HTTPS</code>
                  </header>
                  <div className="field-grid callback-endpoint-grid">
                    <label className="field field-wide">
                      <span>HTTP / HTTPS 回调地址</span>
                      <input
                        name="callbackUrl"
                        type="url"
                        required
                        maxLength={2048}
                        placeholder="https://api.example.com/im/callback"
                        defaultValue={selected?.callbackUrl ?? ""}
                      />
                      <small>支持 HTTP 和 HTTPS 公网地址；禁止内网、本机、元数据地址和重定向。保存和投递时都会校验目标。</small>
                      <small>推荐使用 HTTPS；HTTP 不加密，回调内容可能被窃听或篡改。</small>
                    </label>
                    <label className="field">
                      <span>超时（毫秒）</span>
                      <input
                        name="timeoutMs"
                        type="number"
                        min={100}
                        max={3000}
                        defaultValue={selected?.timeoutMs ?? (selectedType === "MESSAGE_BEFORE_SEND" ? 1000 : 3000)}
                      />
                      <small>允许范围 100–3000 ms</small>
                    </label>
                    <label className="field">
                      <span>最大投递次数</span>
                      <input
                        name="maxAttempts"
                        type="number"
                        min={1}
                        max={8}
                        disabled={selectedType === "MESSAGE_BEFORE_SEND"}
                        defaultValue={selectedType === "MESSAGE_BEFORE_SEND" ? 1 : selected?.maxAttempts ?? 8}
                      />
                      <small>前置回调固定为 1；异步回调最多 8 次</small>
                    </label>
                  </div>
                </section>

                {selectedType.startsWith("MESSAGE_") ? (
                  <section className="callback-form-section">
                    <header className="callback-form-section-heading">
                      <div><h4>事件过滤</h4><p>缩小回调范围；未勾选的维度表示接收全部。</p></div>
                    </header>
                    <div className="callback-subscriptions">
                      <OptionGroup
                        name="conversationTypes"
                        title="会话类型"
                        values={["C2C", "GROUP", "ROOM", "CHANNEL", "BROADCAST"]}
                        selected={selected?.subscribedConversationTypes ?? []}
                      />
                      <OptionGroup
                        name="messageTypes"
                        title="消息类型"
                        values={["TEXT", "IMAGE", "AUDIO", "VIDEO", "FILE", "CUSTOM", "COMMAND", "SYSTEM", "EDIT", "RETRACT"]}
                        selected={selected?.subscribedMessageTypes ?? []}
                      />
                      <OptionGroup
                        name="messageSources"
                        title="消息来源"
                        values={["CLIENT", "SERVER_API"]}
                        selected={selected?.subscribedMessageSources ?? []}
                      />
                    </div>
                  </section>
                ) : null}

                {selectedType === "MESSAGE_BEFORE_SEND" ? (
                  <div className="management-security-note callback-warning">
                    <Icon name="warning" size={16} />
                    <span><strong>失败关闭：</strong>启用后，超时、非 2xx、非法响应、并发饱和或业务拒绝都会阻断消息，且不会分配 seq。</span>
                  </div>
                ) : null}

                <footer className="callback-actions">
                  <Button disabled={readingSecret} loading={busy} type="submit" variant="primary">保存配置</Button>
                  {selected === null ? null : (
                    <div className="callback-secondary-actions">
                      <Button disabled={busy || readingSecret} type="button" onClick={() => { void action("verify"); }}>发送测试事件</Button>
                      <Button disabled={busy || readingSecret} type="button" onClick={() => { if (selected.enabled) setConfirmation("disable"); else void action("enable"); }}>
                        {selected.enabled ? "停用" : "启用"}
                      </Button>
                    </div>
                  )}
                </footer>
              </form>
            )}
          </section>
        </div>
      )}
      {confirmation === "reveal-secret" && application !== null && applicationKey?.configured && canReadSecret ? <ConfirmDialog confirmLabel="查看密钥" consequences={["当前应用的四类回调共用同一个密钥；其他应用使用独立密钥", "仅在当前页面内存中临时保留，60 秒后自动隐藏", "请勿截图、分享或保存到客户端；主动复制后需自行保护剪贴板"]} description={`应用 ${application.appId}；此操作只读取应用回调密钥，不轮换或修改任何类型配置。`} title="确认查看应用回调密钥" onClose={() => { clearSecret(); setConfirmation(null); }} onConfirm={() => { void revealSecret(); }} /> : null}
      {confirmation === "rotate-secret" && application !== null && applicationKey?.configured && canRotateSecret ? <ConfirmDialog busy={busy} confirmLabel="确认轮换" consequences={["将同时影响当前应用全部已配置的回调类型，业务服务端需统一更新验签密钥", "旧密钥会进入有限的兼容窗口，请在窗口结束前更新业务服务端", "各类型的地址、启用状态、过滤和验证状态保持不变", "新密钥仅临时展示，之后可确认后再次查看"]} description={`应用 ${application.appId}；这不是单个回调类型的密钥轮换。`} title="轮换应用回调密钥" onClose={() => { setConfirmation(null); }} onConfirm={() => { void rotateSecret(); }} /> : null}
      {confirmation === "disable" ? <ConfirmDialog busy={busy} confirmLabel="确认停用" consequences={["平台将停止向该端点投递新事件", "已有失败任务仍保留在投递日志中", "可以在完成验证后重新启用"]} description="停用不会删除回调配置和历史投递日志。" title="停用回调" onClose={() => { setConfirmation(null); }} onConfirm={() => { void action("disable"); }} /> : null}
    </div>
  );
}

function callbackState(callback: ApplicationCallbackConfiguration | null | undefined): {
  readonly label: string;
  readonly className: "tag-success" | "tag-info" | "tag-muted" | "tag-danger";
} {
  if (callback === null || callback === undefined) return { label: "未配置", className: "tag-muted" };
  if (callback.enabled) return { label: "已启用", className: "tag-success" };
  if (callback.status === "VERIFIED") return { label: "已验证", className: "tag-info" };
  if (callback.status === "ERROR") return { label: "异常", className: "tag-danger" };
  if (callback.status === "DISABLED") return { label: "已停用", className: "tag-muted" };
  return { label: "待验证", className: "tag-muted" };
}

function formString(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

function formStrings(data: FormData, name: string): readonly string[] {
  return data.getAll(name).filter((value): value is string => typeof value === "string");
}

function OptionGroup({ name, title, values, selected }: {
  readonly name: string;
  readonly title: string;
  readonly values: readonly string[];
  readonly selected: readonly string[];
}) {
  return (
    <fieldset>
      <legend><span>{title}</span><small>不选择表示全部</small></legend>
      <div className="callback-option-list">
        {values.map((value) => (
          <label key={value}>
            <input type="checkbox" name={name} value={value} defaultChecked={selected.includes(value)} />
            <span>{value}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
